/** Farm home: how everything is at a glance, what needs a look, tanks, weather, soil and outputs. */
import { useEffect, useMemo, useState } from "react";
import type { Alert, Device, Reading, Site } from "../api.ts";
import { switchables } from "../control.ts";
import { ago, countByStatus, formatNumber, healthScore, isOnOff, portKey, portLabel, tankLevel, tankPercent, tankRange } from "../format.ts";
import { go } from "../route.ts";
import { ErrorBoundary, Icon, Problem, SavedCopyBanner, Section, Spinner, useLoad, useNow } from "../ui.tsx";
import { InsightsCard } from "./ai.tsx";
import { RainTodayValue, ReadingTile, SoilCard, WindRoseFor } from "./device.tsx";
import { isDeviceHealth, isRainTotal, soilPorts, windPorts } from "../../../../packages/core/src/ports.ts";
import { useHomePins } from "../home-pins.tsx";
import { Ring, Tank } from "../widgets.tsx";
import { AlertRow, useSharedAlerts } from "./alerts.tsx";
import { api, type ProgramView } from "../api.ts";
import { programsPollMs, STATE_TEXT, whereText } from "../programs-text.ts";
import { loadAccount } from "../farm-data.ts";

export function Farm({ accountId, staff }: { accountId: string | undefined; staff: boolean }) {
  const now = useNow();
  const data = useLoad(() => loadAccount(staff ? accountId : undefined), [accountId, staff], 60_000);
  const alerts = useSharedAlerts();
  // The progress of a running program every 15 s; otherwise a start shows within 30 s.
  const [poll, setPoll] = useState(30_000);
  const programs = useLoad(() => api.programs(staff ? accountId : undefined).then((r) => r.programs).catch(() => [] as ProgramView[]), [accountId, staff], poll);
  useEffect(() => setPoll(programsPollMs(programs.data, 15_000)), [programs.data]);

  if (data.error && !data.data) return <Problem error={data.error} onRetry={data.reload} />;
  if (!data.data) return <Spinner />;
  const { devices, sites, latest } = data.data;
  if (!devices.length) return <div className="card empty">No devices on this account yet. FMS adds them during set-up.</div>;
  return (
    <>
      {data.data.savedAt !== undefined && <SavedCopyBanner at={data.data.savedAt} now={now} onRetry={data.reload} />}
      <FarmBody devices={devices} sites={sites} latest={latest} alerts={alerts.data?.alerts ?? []} programs={programs.data ?? []} now={now} accountId={staff ? accountId : undefined} onAcked={alerts.reload} />
    </>
  );
}

function FarmBody(props: { devices: Device[]; sites: Site[]; latest: Map<string, Reading[]>; alerts: Alert[]; programs: ProgramView[]; now: number; accountId: string | undefined; onAcked: () => void }) {
  const { devices, latest, alerts, now } = props;
  const counts = countByStatus(devices, now);
  const open = alerts.filter((a) => !a.acked);
  // Acknowledged means someone's onto it, not that it's fixed: a device still offline stays
  // on the home (marked acknowledged) until it's back.
  const known = alerts.filter((a) => a.acked && a.ongoing);
  const score = healthScore(devices, alerts, now);
  const siteName = new Map(props.sites.map((s) => [s.id, s.name]));
  const value = (d: Device, port: string) => latest.get(d.devEui)?.find((r) => r.port === port);

  const tanks = useMemo(
    () =>
      devices.flatMap((d) =>
        d.ports
          .filter((p) => p.enabled && tankRange(p))
          .map((p) => {
            const r = value(d, portKey(p));
            return r ? { d, label: portLabel(d, portKey(p)), r, level: tankLevel(p, r.value), pct: tankPercent(p, r.value)! } : null;
          })
          .filter((x) => x !== null),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [devices, latest],
  );
  const portsOf = (d: Device) => (latest.get(d.devEui) ?? []).map((r) => r.port);
  // Weather stations: Milesight WTS506s and Modbus stations on FMS boards (anything with a wind direction).
  const weather = devices.filter((d) => latest.get(d.devEui)?.length && (d.model === "wts506" || windPorts(d, portsOf(d)).direction));
  const soil = devices.filter((d) => soilPorts(d, portsOf(d)).some((p) => value(d, p)));
  const outputs = devices.flatMap((d) => switchables(d, latest.get(d.devEui) ?? []).map((o) => ({ d, o })));
  // Only count what's reported recently as running; an offline device's last state may be stale.
  const running = outputs.filter((x) => x.o.on && x.o.at !== undefined && now - x.o.at < 2 * 3_600_000);

  return (
    <>
      {/* Each card on its own: one that breaks (odd data from a device) leaves the rest showing. */}
      <ErrorBoundary>
      <section className="card hero">
        <Ring value={score} />
        <div>
          <h1>{open.length ? `${open.length} thing${open.length === 1 ? "" : "s"} to look at` : known.length ? "Nothing new to look at" : "All good"}</h1>
          <p>
            {counts.online} of {devices.length} devices reporting
            {counts.late ? `, ${counts.late} late` : ""}
            {counts.offline ? `, ${counts.offline} offline` : ""}.{" "}
            {known.length ? `${known.length} acknowledged, still going. ` : ""}
            {running.length ? `${running.length} output${running.length === 1 ? "" : "s"} running.` : "Nothing running."}
          </p>
          <div className="stats">
            <span>
              <b>{open.filter((a) => a.severity === "crit").length}</b>urgent
            </span>
            <span>
              <b>{open.filter((a) => a.severity === "warn").length}</b>check soon
            </span>
            <span>
              <b>{props.sites.length}</b>site{props.sites.length === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </section>
      </ErrorBoundary>

      <ErrorBoundary>
        <PinnedReadings devices={devices} latest={latest} now={now} />
      </ErrorBoundary>

      {open.length + known.length > 0 && (
        <ErrorBoundary>
        <Section
          title="Needs a look"
          right={
            <button className="btn ghost" onClick={() => (location.hash = "#/alerts")}>
              All alerts
            </button>
          }
        >
          <div className="card list">
            {[...open, ...known].slice(0, 4).map((a) => (
              <AlertRow key={a.id} a={a} now={now} site={siteName.get(a.siteId)} accountId={props.accountId} onAcked={props.onAcked} compact />
            ))}
          </div>
        </Section>
        </ErrorBoundary>
      )}

      <ErrorBoundary>
        <InsightsCard accountId={props.accountId} staff={props.accountId !== undefined} />
      </ErrorBoundary>

      {props.programs.some((v) => v.run.state !== "idle") && (
        <ErrorBoundary>
        <Section
          title="Programs"
          right={
            <button className="btn ghost" onClick={() => go({ name: "programs" })}>
              All programs
            </button>
          }
        >
          <div className="card list">
            {props.programs
              .filter((v) => v.run.state !== "idle")
              .map((v) => {
                const st = STATE_TEXT[v.run.state];
                const dev = (eui: string) => devices.find((d) => d.devEui === eui);
                return (
                  <button key={v.program.id} className="row row-btn" onClick={() => go({ name: "programs" })}>
                    <span className={`dot ${st.tone}`} />
                    <div>
                      <div className="t">{v.program.name}</div>
                      <div className="d">{whereText(dev, v.program, v.run, now) || (v.run.reason ?? "")}</div>
                      {(v.run.state === "running" || v.run.state === "paused") && (
                        <div className="progress" style={{ marginTop: 6 }}>
                          <div style={{ width: `${v.progress}%` }} />
                        </div>
                      )}
                    </div>
                    <span className={`pill ${st.tone}`}>{st.label}</span>
                  </button>
                );
              })}
          </div>
        </Section>
        </ErrorBoundary>
      )}

      {tanks.length > 0 && (
        <ErrorBoundary>
        <Section title="Tanks">
          <div className="tiles tanks">
            {tanks.map((t) => (
              <button key={`${t.d.devEui}${t.r.port}`} className="tile tank" onClick={() => go({ name: "device", eui: t.d.devEui })}>
                <Tank percent={t.pct} label={t.label} />
                <div className="lbl">{t.label}</div>
                <div className="foot">
                  {formatNumber(t.level)} {t.r.unit ?? ""} · {t.d.name} · {ago(t.r.at, now)}
                </div>
              </button>
            ))}
          </div>
        </Section>
        </ErrorBoundary>
      )}

      {weather.map((d) => {
        const w = windPorts(d, portsOf(d));
        // A station's readings by what they are, whatever the port is called (Milesight names or Modbus points).
        const find = (re: RegExp, unit?: RegExp) =>
          (latest.get(d.devEui) ?? []).find((r) => !isDeviceHealth(r.port) && r.port !== w.direction && (re.test(r.port) || re.test(portLabel(d, r.port))) && (!unit || unit.test(r.unit ?? "")));
        const stats: Array<[string, Reading | undefined]> = [
          ["Air", find(/temp/i, /°C|^C$/i)],
          ["Humidity", find(/humid/i)],
          ["Wind", w.speed ? value(d, w.speed) : find(/wind.?speed/i)],
          ["Gusts", find(/gust/i)],
          ["Rain today", (latest.get(d.devEui) ?? []).find((r) => isRainTotal(d, r.port))],
          ["Pressure", find(/pressure|baro/i, /hpa|mbar/i)],
          ["Solar", find(/solar|radiation/i)],
        ];
        return (
          <ErrorBoundary key={d.devEui}>
          <Section title={d.name}>
            <button className="card pad weather-card" onClick={() => go({ name: "device", eui: d.devEui })}>
              {w.direction ? <WindRoseFor device={d} direction={w.direction} /> : <span />}
              <div className="wx-stats">
                {stats.map(([l, r]) =>
                  r ? (
                    <div key={l}>
                      <span>{l}</span>
                      {l === "Rain today" ? (
                        <RainTodayValue device={d} port={r.port} />
                      ) : (
                        <>
                          <b>{formatNumber(r.value)}</b> <small>{r.unit}</small>
                        </>
                      )}
                    </div>
                  ) : null,
                )}
              </div>
            </button>
          </Section>
          </ErrorBoundary>
        );
      })}

      {soil.length > 0 && (
        <Section title="Soil moisture">
          <div className="soil-grid">
            {soil.map((d) => (
              <ErrorBoundary key={d.devEui}>
                <SoilCard device={d} ports={soilPorts(d, portsOf(d))} title={d.name} />
              </ErrorBoundary>
            ))}
          </div>
        </Section>
      )}

      {outputs.length > 0 && (
        <ErrorBoundary>
        <Section title="Pumps and outputs">
          <div className="card list">
            {outputs.map(({ d, o }) => (
              <button key={`${d.devEui}${o.key}`} className="row row-btn" onClick={() => go({ name: "device", eui: d.devEui })}>
                <span className={`dot ${o.on ? "good" : ""}`} />
                <div>
                  <div className="t">{o.label}</div>
                  <div className="d">
                    {d.name}
                    {o.at ? ` · ${ago(o.at, now)}` : ""}
                  </div>
                </div>
                <span className={`onoff ${o.on ? "on" : ""}`}>
                  {o.on === undefined ? "?" : o.on ? "On" : "Off"}
                  {o.at !== undefined && now - o.at > 2 * 3_600_000 ? <span className="muted small"> (last known)</span> : null}
                </span>
              </button>
            ))}
          </div>
          <p className="muted small" style={{ margin: 0 }}>
            Open a device to switch it.
          </p>
        </Section>
        </ErrorBoundary>
      )}
    </>
  );
}

/** The readings this person pinned from device pages, in their order. */
function PinnedReadings({ devices, latest, now }: { devices: Device[]; latest: Map<string, Reading[]>; now: number }) {
  const home = useHomePins();
  const [editing, setEditing] = useState(false);
  const shown = home.pins
    .map((pin, index) => {
      const d = devices.find((x) => x.devEui === pin.devEui);
      const r = latest.get(pin.devEui)?.find((x) => x.port === pin.port);
      return d && r ? { d, index, r: { ...r, label: portLabel(d, r.port), onOff: isOnOff(d, r.port) } } : null;
    })
    .filter((x) => x !== null);
  return (
    <Section
      title="Pinned"
      right={
        shown.length > 0 && (
          <button className="btn ghost" onClick={() => setEditing(!editing)}>
            {editing ? "Done" : "Edit"}
          </button>
        )
      }
    >
      {home.error && <div className="error small">{home.error}</div>}
      {shown.length ? (
        <div className={`tiles pinned${editing ? " editing" : ""}`}>
          {shown.map(({ d, r, index }) => (
            <div key={`${d.devEui}${r.port}`} className="pinned-cell">
              <ReadingTile device={d} r={r} now={now} onPick={() => go({ name: "device", eui: d.devEui })} footer={d.name} />
              {editing && (
                <div className="pin-tools">
                  <button aria-label={`Move ${r.label} earlier`} disabled={index === 0} onClick={() => home.move(index, -1)}>
                    <Icon name="back" size={16} />
                  </button>
                  <button aria-label={`Move ${r.label} later`} disabled={index === home.pins.length - 1} onClick={() => home.move(index, 1)}>
                    <Icon name="chev" size={16} />
                  </button>
                  <button aria-label={`Remove ${r.label}`} onClick={() => home.toggle(d.devEui, r.port)}>
                    <Icon name="close" size={16} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="card empty small">
          Put the readings you check most here: open a device and tap the <Icon name="pin" size={14} /> on any reading.
        </div>
      )}
    </Section>
  );
}
